import type { TbHttp } from "./http";
import type { TbSubscriptionManager, TsValue } from "./ws";
import { getAttributes } from "./telemetry";
import type { TbSiteContext } from "./topologyFromTb";
import type { BessUnitSnapshot, ConnectionState, SiteSnapshot } from "../types";

/**
 * Live SiteSnapshot assembler — wired against the real CPC Inox tenant
 * shape per [`TB_FINDINGS.md`](../../../TB_FINDINGS.md).
 *
 * Key TB → operator-spa mappings:
 *
 *   Grid device (canonical SIGN_CONVENTION — positive = importing):
 *     grid_kW    → grid.active_W       (× 1000; no sign flip)
 *     grid_kVAr  → grid.reactive_VAr   (× 1000)
 *     total_kW   → building_load_W     (× 1000; authoritative site-load
 *                                       reading, falls back to a
 *                                       conservation-of-energy estimate
 *                                       when the Grid device doesn't
 *                                       publish it — e.g. Gessate)
 *
 *   Transformer device (BESS-AUX-DEVICES) — Basiano-style sites:
 *     frequency_Hz / voltage_V / PF_total → grid.frequency_Hz / .voltage_V / .pf
 *     (Gessate has no transformer; those fields stay NaN.)
 *
 *   Controller device:
 *     ctrl_pwr_kW            → controller.pid_setpoint_W (× 1000)
 *     ctrl_pwr_kW_{2,3,4}    → bess_per_unit[1..3].setpoint_W
 *     (first BESS uses the un-suffixed key — TB convention.)
 *
 *   BESS device:
 *     battery_kW             → bess_per_unit[i].active_W (× -1000)
 *     battery_kVAr           → bess_per_unit[i].reactive_VAr (× 1000)
 *     battery_soc_%          → soc_pct
 *     battery_soh_%          → (future field — surfaced via tooltip)
 *     Average_cell_temp      → ambient_temperature_C (× 0.01 + sanitise > 600)
 *     SERVER_SCOPE attribute  EP_d_consumption → energy_today.bess_charge_kWh
 *     SERVER_SCOPE attribute  EN_d_consumption → energy_today.bess_discharge_kWh
 *
 *   Wildcard cross-contamination on BESS devices (grid_kW, ctrl_pwr_kW)
 *   is IGNORED — those keys leak from cloud-connector wildcard subs and
 *   are not the BESS's own values.
 */
/**
 * If a BESS's `battery_kW` source-timestamp is older than this, the unit is
 * considered offline regardless of any other key it may still be publishing.
 * Rationale: the cloud-connector batches uplinks every ~2 min, so 15 min ≈ 7
 * missed batches — well beyond a transient hiccup, but tight enough that a
 * genuine comms outage shows in the dashboard before the next shift change.
 */
const BESS_BATTERY_KW_STALE_MS = 15 * 60_000;

/**
 * snapshot.connection staleness threshold (DATA-04): "live" while the newest
 * telemetry SOURCE ts across all WS handlers is younger than this, "stale"
 * beyond. 240 s = 2× the ~2-min cloud-connector uplink cadence — one missed
 * batch keeps us "live", two degrade honestly to "stale".
 */
const LIVE_SOURCE_STALE_MS = 240_000;

export class TbSnapshotAssembler {
  private snapshot: SiteSnapshot;
  private disposers: Array<() => void> = [];
  private listeners = new Set<(s: SiteSnapshot) => void>();
  private emitTimer: number | null = null;
  private attrTimer: number | null = null;
  private dirty = false;
  /** Last `total_kW` reading from the Grid device, in watts (SPA sign:
   *  positive = consumption from the site). When the Grid publishes it
   *  this is the authoritative site-load measurement and overrides the
   *  energy-conservation fallback below. */
  private gridTotalLoadW: number | null = null;
  /**
   * Per-BESS source-timestamp of the most recently received finite `battery_kW`
   * (TB's own ts, NOT wall-clock — robust to network lag and immune to TB
   * retained-message replay on reconnect). Drives the staleness check in
   * {@link evaluateBessStaleness}; a unit that has NEVER received `battery_kW`
   * is intentionally absent from this map so the initial offline state survives
   * the first few flush ticks (no false "offline" flash on page load).
   */
  private lastBatteryKwTs = new Map<string, number>();
  /**
   * Newest telemetry SOURCE ts (ms epoch, TB's own stamp) seen across ALL
   * WS handlers. Drives snapshot.ts (DATA-05 — the data-age pip must age
   * with the data, not with attribute-poll wall-clock) and the live/stale
   * split of snapshot.connection. 0 until the first sample lands.
   */
  private newestSourceTs = 0;
  /**
   * Per-key freshness map (DATA-08) — newest SOURCE ts per top-level value
   * the UI renders. Exposed on the snapshot as `freshness` (same object,
   * mutated in place like the rest of the snapshot — the store clones each
   * recent[] entry). Key names are documented on {@link SiteSnapshot}.
   */
  private readonly freshness: Record<string, number> = {};
  /** True while the TB WebSocket is down → snapshot.connection "offline". */
  private wsDown = false;
  /** Wall-clock of start() — staleness baseline until the first sample. */
  private startedAt = Date.now();

  constructor(
    private readonly http: TbHttp,
    private readonly ws: TbSubscriptionManager,
    private readonly site: TbSiteContext,
  ) {
    this.snapshot = this.initialSnapshot();
  }

  start(): void {
    this.startedAt = Date.now();
    this.wireWsState();
    this.wireGrid();
    this.wireTransformer();
    this.wireController();
    this.wireBess();
    this.wirePv();
    this.refreshAttributes(); // initial
    this.attrTimer = window.setInterval(() => this.refreshAttributes(), 30_000);
    this.emitTimer = window.setInterval(() => this.flush(), 1000);
  }
  stop(): void {
    for (const d of this.disposers) try { d(); } catch { /* */ }
    this.disposers = [];
    if (this.attrTimer) window.clearInterval(this.attrTimer);
    if (this.emitTimer) window.clearInterval(this.emitTimer);
    this.attrTimer = null;
    this.emitTimer = null;
  }

  onSnapshot(cb: (s: SiteSnapshot) => void): () => void {
    this.listeners.add(cb);
    cb(this.snapshot);
    return () => this.listeners.delete(cb);
  }

  latest(): SiteSnapshot { return this.snapshot; }

  /* ----------------------------- subscriptions ----------------------------- */

  /**
   * Socket-down detection for `snapshot.connection === "offline"` (DATA-04).
   *
   * `TbSubscriptionManager.onStateChange` is being added separately
   * (DATA-02, cluster A); the optional chain keeps this assembler working
   * against managers that don't expose it yet — in that case "offline"
   * simply never fires from here (the store's connection pill stays the
   * only socket-down signal) and staleness still degrades the snapshot to
   * "stale" after {@link LIVE_SOURCE_STALE_MS}.
   */
  private wireWsState(): void {
    const ws = this.ws as TbSubscriptionManager & {
      onStateChange?: (cb: (state: string) => void) => (() => void) | void;
    };
    const dispose = ws.onStateChange?.((state) => {
      if (state === "open") {
        if (this.wsDown) { this.wsDown = false; this.dirty = true; }
      } else if (state === "reconnecting" || state === "closed") {
        if (!this.wsDown) { this.wsDown = true; this.dirty = true; }
      }
      // "connecting" is transitional (a retry in flight) — keep last state.
    });
    if (typeof dispose === "function") this.disposers.push(dispose);
  }

  /**
   * Bump the newest-source-ts watermark with every key of a WS batch —
   * called at the top of every handler so even untracked keys (transformer
   * volts, temperatures, …) count as "the site published telemetry".
   */
  private noteSourceTs(vals: Record<string, TsValue>): void {
    // Reject timestamps more than 60 s in the FUTURE (skewed edge clock):
    // a future-pinned watermark would claim "live" forever and, via
    // snapshot.ts, purge the store's recent[] buffer.
    const maxTs = Date.now() + 60_000;
    for (const v of Object.values(vals)) {
      if (v && Number.isFinite(v.ts) && v.ts > this.newestSourceTs && v.ts <= maxTs) {
        this.newestSourceTs = v.ts;
      }
    }
  }

  /** Record per-key freshness (DATA-08). Call only where a value is ACCEPTED
   *  into the snapshot, so the map mirrors what the UI actually renders. */
  private stamp(key: string, v: TsValue | undefined): void {
    if (!v || !Number.isFinite(v.ts)) return;
    if (v.ts > Date.now() + 60_000) return; // future-skewed source clock
    const prev = this.freshness[key];
    if (prev === undefined || v.ts > prev) this.freshness[key] = v.ts;
  }

  private wireGrid() {
    if (!this.site.refs.grid) return;
    const dispose = this.ws.subscribe(
      this.site.refs.grid,
      ["grid_kW", "grid_kVAr", "total_kW"],
      (vals) => {
        this.noteSourceTs(vals);
        const grid_kW = num(vals.grid_kW);
        if (Number.isFinite(grid_kW)) {
          // Both the EMS server and the SPA use the canonical SIGN_CONVENTION
          // — positive grid active power = importing from the grid. The
          // cloud-connector normalises CT polarity at the source layer
          // (smartmeter-aggregation `factor`); no flip is needed here.
          this.snapshot.grid.active_W = grid_kW * 1000;
          this.snapshot.net_at_meter_W = grid_kW * 1000;
          this.stamp("grid.active_W", vals.grid_kW);
          this.dirty = true;
        }
        const grid_kVAr = num(vals.grid_kVAr);
        if (Number.isFinite(grid_kVAr)) {
          this.snapshot.grid.reactive_VAr = grid_kVAr * 1000;
          this.stamp("grid.reactive_VAr", vals.grid_kVAr);
          this.dirty = true;
        }
        // `total_kW` on the Grid device is the cumulative active-power
        // measurement across all phases — i.e. the site load. Published
        // with the canonical convention (positive = consumption), same
        // as grid_kW, so scaling to watts is enough.
        const total_kW = num(vals.total_kW);
        if (Number.isFinite(total_kW)) {
          this.gridTotalLoadW = total_kW * 1000;
          this.stamp("building_load_W", vals.total_kW);
          this.dirty = true;
        }
        this.recomputeLoad();
      },
    );
    this.disposers.push(dispose);
  }

  /**
   * Net building load — SINGLE source of truth is the Grid device's
   * `total_kW` key (cumulative active power across phases at the
   * metering point). We never derive load from grid + pv − bess
   * here, because a silently-switching source would produce a
   * value that disagrees with the smartmeter at sites where
   * sub-meter drift, parasitic loads, or PV losses introduce a
   * gap.
   *
   * Sign is preserved — positive = net consumption (the building
   * is drawing more than it produces), negative = net production
   * (on-site PV / co-gen exceeds the load and the building is a
   * net source). The field name `building_load_W` is kept for
   * backwards compatibility with every chart / panel that already
   * consumes it; the operator-facing label is "Net building load".
   *
   * Until `total_kW` has been received, the snapshot's value stays
   * at its initial 0 — the UI shows a clean "no data yet" rather
   * than a misleading approximation.
   */
  private recomputeLoad() {
    if (this.gridTotalLoadW !== null && Number.isFinite(this.gridTotalLoadW)) {
      this.snapshot.building_load_W = this.gridTotalLoadW;
      this.dirty = true;
    }
  }

  private wireTransformer() {
    if (!this.site.refs.aux?.length) return;
    // Subscribe only to the first auxiliary device for grid quality metrics;
    // they're all reporting nearly identical MV-side values.
    const ref = this.site.refs.aux[0];
    const dispose = this.ws.subscribe(
      ref,
      ["frequency_Hz", "voltage_V", "PF_total", "current_A"],
      (vals) => {
        this.noteSourceTs(vals);
        const f = num(vals.frequency_Hz);
        if (Number.isFinite(f)) { this.snapshot.grid.frequency_Hz = f; this.dirty = true; }
        const v = num(vals.voltage_V);
        if (Number.isFinite(v)) { this.snapshot.grid.voltage_V = v; this.dirty = true; }
        const pf = num(vals.PF_total);
        if (Number.isFinite(pf)) { this.snapshot.grid.pf = Math.abs(pf); this.dirty = true; }
        const i = num(vals.current_A);
        if (Number.isFinite(i)) { this.snapshot.grid.current_A = i; this.dirty = true; }
      },
    );
    this.disposers.push(dispose);
  }

  private wireController() {
    if (!this.site.refs.controller) return;
    const dispose = this.ws.subscribe(
      this.site.refs.controller,
      [
        // Setpoint keys (existing)
        "ctrl_pwr_kW", "ctrl_pwr_kVAr",
        "ctrl_pwr_kW_2", "ctrl_pwr_kVAr_2",
        "ctrl_pwr_kW_3", "ctrl_pwr_kVAr_3",
        "ctrl_pwr_kW_4", "ctrl_pwr_kVAr_4",
        // controller/diagnostics keys (added by EDGE_FIXES.md §2.2)
        "mode_active", "mode_reactive",
        "auto_bess_on",
        "pid_actively_controlling",
        "schedule_source",
        "pid_setpoint_W", "pid_setpoint_VAr",
      ],
      (vals) => {
        this.noteSourceTs(vals);
        // First BESS uses the un-suffixed key per TB convention.
        const total = num(vals.ctrl_pwr_kW);
        if (Number.isFinite(total)) {
          this.snapshot.controller.pid_setpoint_W = total * 1000;
          this.stamp("controller.pid_setpoint_W", vals.ctrl_pwr_kW);
          // First BESS setpoint
          if (this.snapshot.bess_per_unit[0]) {
            // If there are no _2/_3/_4 keys (single-BESS site), the un-
            // suffixed key IS the per-unit setpoint. With multi-BESS we
            // use it as aggregate fallback only.
            if (this.snapshot.bess_per_unit.length === 1) {
              this.snapshot.bess_per_unit[0].setpoint_W = total * 1000;
            }
          }
          this.dirty = true;
        }
        for (let i = 2; i <= 8; i++) {
          const key = `ctrl_pwr_kW_${i}` as const;
          const v = num(vals[key]);
          if (!Number.isFinite(v)) continue;
          const unit = this.snapshot.bess_per_unit[i - 1];
          if (unit) {
            unit.setpoint_W = v * 1000;
            this.dirty = true;
          }
        }
        // First BESS in multi-BESS sites: deduce from total minus the rest.
        if (this.snapshot.bess_per_unit.length > 1 && Number.isFinite(total)) {
          const restSum = this.snapshot.bess_per_unit
            .slice(1)
            .reduce((s, u) => s + (u.setpoint_W ?? 0), 0) / 1000;
          this.snapshot.bess_per_unit[0].setpoint_W = (total - restSum) * 1000;
          this.dirty = true;
        }

        // Diagnostics fan-out (only when cloud-connector with the new
        // `value_field` extraction has been deployed).
        const mode = vals.mode_active?.value;
        if (typeof mode === "string" && mode) {
          const m = mode as import("../types").ControllerMode;
          this.snapshot.controller.active_mode = m;
          this.stamp("controller.active_mode", vals.mode_active);
          this.dirty = true;
        }
        const auto = vals.auto_bess_on?.value;
        if (auto !== undefined && auto !== null) {
          this.snapshot.controller.auto_bess_on =
            typeof auto === "boolean" ? auto : Number(auto) >= 0.5;
          this.dirty = true;
        }
        const tracking = vals.pid_actively_controlling?.value;
        if (tracking !== undefined && tracking !== null) {
          this.snapshot.controller.pid_actively_controlling =
            typeof tracking === "boolean" ? tracking : Number(tracking) >= 0.5;
          this.dirty = true;
        }
        const src = vals.schedule_source?.value;
        if (typeof src === "string") {
          const s = src as "cloud" | "local" | "manual_override";
          if (s === "cloud" || s === "local" || s === "manual_override") {
            this.snapshot.controller.schedule_source = s;
            this.dirty = true;
          }
        }
        const sp_w = num(vals.pid_setpoint_W);
        if (Number.isFinite(sp_w)) {
          // value_field cloud-connector applied scaling_factor: 0.001 → kW
          this.snapshot.controller.pid_setpoint_W = sp_w * 1000;
          this.stamp("controller.pid_setpoint_W", vals.pid_setpoint_W);
          this.dirty = true;
        }
      },
    );
    this.disposers.push(dispose);
  }

  private wireBess() {
    const bessIds = Object.keys(this.site.refs.bess);
    bessIds.forEach((bessId, idx) => {
      const ref = this.site.refs.bess[bessId];
      const dispose = this.ws.subscribe(
        ref,
        [
          // Canonical BESS keys
          "battery_kW", "battery_kVAr",
          "battery_soc_%", "battery_soh_%",
          // DC bus voltage — different cloud-connector deployments use
          // different names. Subscribe to ALL candidates; the handler
          // picks whichever returns a finite value.
          "total_voltage_V",
          "total_voltage",
          "Total_Voltage",
          "dc_voltage",
          // Temperature aliases — the cloud-connector publishes the RAW Zhiguang
          // `ambient_temperature` (already °C) on the live fleet; the clean
          // `ambient_temperature_C` / `cell_temp_C` keys are the intended
          // contract but aren't published yet. Subscribe to all; the handler
          // prefers them in order.
          "ambient_temperature_C",
          "ambient_temperature",
          "cell_temp_C",
          "fault_count",
          "bess_onoff",
          // Pre-edge-fix fallback: raw Zhiguang IEC-104 cell temp key
          "Average_cell_temp",
        ],
        (vals) => {
          this.noteSourceTs(vals);
          const unit = this.snapshot.bess_per_unit[idx];
          if (!unit) return;
          const kw = num(vals.battery_kW);
          if (Number.isFinite(kw)) {
            // TB convention: positive = export from BESS (discharging).
            // SPA canonical: positive = charging.
            unit.active_W = -kw * 1000;
            unit.online = true;
            // Fresh heartbeat — clear any age left over from a prior
            // offline spell (the staleness sweep re-derives the true age
            // within 1 s if this sample is actually a stale replay).
            unit.heartbeat_age_s = 0;
            // Stamp the SOURCE ts (not wall-clock) for the staleness check.
            // `vals.battery_kW.ts` is the cloud-connector's own timestamp at
            // uplink — what we want for "did the BESS publish recently?".
            if (vals.battery_kW) this.lastBatteryKwTs.set(bessId, vals.battery_kW.ts);
            this.stamp(`bess.${unit.id}.active_W`, vals.battery_kW);
            this.stamp("bess_aggregate.active_W", vals.battery_kW);
            this.dirty = true;
          }
          const kvar = num(vals.battery_kVAr);
          if (Number.isFinite(kvar)) {
            unit.reactive_VAr = kvar * 1000;
            this.dirty = true;
          }
          const soc = num(vals["battery_soc_%"]);
          if (Number.isFinite(soc)) {
            unit.soc_pct = soc;
            this.stamp(`bess.${unit.id}.soc_pct`, vals["battery_soc_%"]);
            this.dirty = true;
          }

          // DC bus voltage — pick the first finite value among the
          // candidate keys. Different cloud-connector versions deliver
          // it under different names; the SPA stays compatible by
          // trying them in order of preference.
          const dcV = firstFinite([
            num(vals.total_voltage_V),
            num(vals.total_voltage),
            num(vals.Total_Voltage),
            num(vals.dc_voltage),
          ]);
          if (Number.isFinite(dcV)) {
            unit.total_voltage_V = dcV;
            this.dirty = true;
          }

          // Ambient (enclosure) temperature. Preference order, first finite wins:
          //   1. ambient_temperature_C — clean explicit key (forward-compat; not
          //      published on the current Zhiguang fleet but kept for edge
          //      versions / sites that do publish it).
          //   2. ambient_temperature   — the RAW Zhiguang key the cloud-connector
          //      actually publishes today (already °C, fresh on Gessate+Basiano).
          //      Without this the assembler fell through to Average_cell_temp —
          //      a CELL temp in centi-°C that was STALE-AT-0 on every Gessate
          //      BESS → "0.0 °C" on the dashboard (Basiano happened to have a
          //      fresh value so it rendered, but as cell temp, not ambient).
          //   3. cell_temp_C           — explicit cell temp (scaled at the edge).
          //   4. Average_cell_temp     — raw Zhiguang cell temp; °C when <600,
          //      else centi-°C (÷100). Last resort.
          const ambC = num(vals.ambient_temperature_C);
          const ambRaw = num(vals.ambient_temperature);
          if (Number.isFinite(ambC)) {
            unit.ambient_temperature_C = ambC;
            this.dirty = true;
          } else if (Number.isFinite(ambRaw)) {
            // Already °C; guard against an accidental centi-°C publish (>600).
            unit.ambient_temperature_C = ambRaw > 600 ? ambRaw / 100 : ambRaw;
            this.dirty = true;
          } else {
            const cellC = num(vals.cell_temp_C);
            if (Number.isFinite(cellC) && cellC < 600) {
              unit.ambient_temperature_C = cellC;
              this.dirty = true;
            } else {
              const tempRaw = num(vals.Average_cell_temp);
              if (Number.isFinite(tempRaw)) {
                const tempC = tempRaw < 600 ? tempRaw : tempRaw / 100;
                unit.ambient_temperature_C = tempC > 600 ? NaN : tempC;
                this.dirty = true;
              }
            }
          }

          // Fault count — single rolled-up key from the edge (preferred)
          const fc = num(vals.fault_count);
          if (Number.isFinite(fc)) {
            unit.fault_count = Math.max(0, Math.round(fc));
            this.dirty = true;
          }

          // BESS ON/OFF state (now mirrored to per-BESS device after edge fix)
          const onoff = vals.bess_onoff;
          if (onoff?.value !== undefined && onoff.value !== null) {
            const v = typeof onoff.value === "boolean" ? onoff.value : Number(onoff.value) >= 0.5;
            unit.bess_onoff = v;
            this.dirty = true;
          }

          this.recomputeAggregate();
        },
      );
      this.disposers.push(dispose);
    });
  }

  private wirePv() {
    if (!this.site.refs.pv) return;
    const dispose = this.ws.subscribe(this.site.refs.pv, ["pv_power_kW"], (vals) => {
      this.noteSourceTs(vals);
      const v = num(vals.pv_power_kW);
      if (!Number.isFinite(v)) return;
      if (!this.snapshot.pv) this.snapshot.pv = { active_W: 0, today_kWh: 0, rated_kWp: 0 };
      this.snapshot.pv.active_W = v * 1000;
      this.stamp("pv.active_W", vals.pv_power_kW);
      this.dirty = true;
      this.recomputeLoad();
    });
    this.disposers.push(dispose);
  }

  /** SERVER_SCOPE attribute poll for daily totals + alarm aggregate. */
  private async refreshAttributes() {
    try {
      // Per-BESS daily totals
      const bessIds = Object.keys(this.site.refs.bess);
      let charge_kWh = 0, discharge_kWh = 0;
      for (const id of bessIds) {
        const a = await getAttributes(this.http, this.site.refs.bess[id], "SERVER_SCOPE",
          ["EP_d_consumption", "EN_d_consumption"]).catch(() => []);
        const ep = readNum(a, "EP_d_consumption");
        const en = readNum(a, "EN_d_consumption");
        if (Number.isFinite(ep)) charge_kWh += ep!;
        if (Number.isFinite(en)) discharge_kWh += en!;
      }
      this.snapshot.energy_today.bess_charge_kWh = charge_kWh;
      this.snapshot.energy_today.bess_discharge_kWh = discharge_kWh;

      // Site-level alarm aggregate + daily totals (single REST call, all
      // attributes are SERVER_SCOPE on the site asset). We try a bunch
      // of common attribute names — whichever the EMS server publishes
      // is what gets used; unknown ones are skipped silently. The
      // SoH/StoryCards components already handle missing values with
      // honest "—" placeholders.
      const siteAttrs = await getAttributes(this.http, this.site.site, "SERVER_SCOPE", [
        // Alarms
        "alarmsCount", "unackedAlarmsCount", "highestAlarmSeverity",
        // Daily energy totals (alternative names depending on which
        // analytics module published them).
        "grid_import_d", "grid_export_d", "pv_d_generation",
        "pv_d", "EP_d", "EN_d",
        // Operator-facing aggregates if the EMS analytics computes them.
        "money_saved_d", "savings_eur_d",
        "co2_avoided_d", "co2_d_kg",
        // Self-consumption / self-sufficiency
        "self_consumption_pct_d", "self_sufficiency_pct_d",
      ]).catch(() => []);
      const total = readNum(siteAttrs, "alarmsCount") ?? 0;
      const sev = readStr(siteAttrs, "highestAlarmSeverity") ?? "NONE";
      this.snapshot.health.overall =
        sev === "CRITICAL" || sev === "MAJOR" ? "critical"
        : sev === "MINOR" || sev === "WARNING" ? "warn"
        : total > 0 ? "warn" : "ok";

      // Daily totals — first match wins (different EMS deployments use
      // different attribute conventions).
      const gridImport = readNum(siteAttrs, "grid_import_d") ?? readNum(siteAttrs, "EP_d");
      const gridExport = readNum(siteAttrs, "grid_export_d") ?? readNum(siteAttrs, "EN_d");
      const pvKwh      = readNum(siteAttrs, "pv_d_generation") ?? readNum(siteAttrs, "pv_d");
      if (Number.isFinite(gridImport)) this.snapshot.energy_today.grid_import_kWh = gridImport!;
      if (Number.isFinite(gridExport)) this.snapshot.energy_today.grid_export_kWh = gridExport!;
      if (Number.isFinite(pvKwh))      this.snapshot.energy_today.pv_kWh          = pvKwh!;

      const money = readNum(siteAttrs, "money_saved_d") ?? readNum(siteAttrs, "savings_eur_d");
      const co2   = readNum(siteAttrs, "co2_avoided_d")  ?? readNum(siteAttrs, "co2_d_kg");
      if (Number.isFinite(money)) this.snapshot.energy_today.money_saved_eur = money!;
      if (Number.isFinite(co2))   this.snapshot.energy_today.co2_avoided_kg  = co2!;

      const sc = readNum(siteAttrs, "self_consumption_pct_d");
      const ss = readNum(siteAttrs, "self_sufficiency_pct_d");
      if (Number.isFinite(sc)) this.snapshot.energy_today.self_consumption_pct = sc!;
      if (Number.isFinite(ss)) this.snapshot.energy_today.self_sufficiency_pct = ss!;

      this.dirty = true;
    } catch {
      // Non-fatal — keep previous values.
    }
  }

  private recomputeAggregate() {
    const units = this.snapshot.bess_per_unit;
    const online = units.filter((u) => u.online);
    const total = online.reduce((s, u) => s + u.active_W, 0);
    this.snapshot.bess_aggregate.active_W = total;
    if (online.length) {
      this.snapshot.bess_aggregate.soc_avg_pct = online.reduce((s, u) => s + u.soc_pct, 0) / online.length;
      this.snapshot.bess_aggregate.soc_min_pct = Math.min(...online.map((u) => u.soc_pct));
      this.snapshot.bess_aggregate.soc_max_pct = Math.max(...online.map((u) => u.soc_pct));
      this.snapshot.bess_aggregate.online_count = online.length;
      const temps = online.map((u) => u.ambient_temperature_C).filter((t) => Number.isFinite(t));
      if (temps.length) this.snapshot.bess_aggregate.avg_temperature_C = temps.reduce((s, t) => s + t, 0) / temps.length;
    }
    // Building load is sourced exclusively from the Grid device's
    // `total_kW`, so battery aggregate changes don't affect it. The
    // recomputeLoad call here is a no-op for the load value itself —
    // kept as a defensive hook in case the source ever changes back.
    this.recomputeLoad();
  }

  /**
   * Per-tick staleness sweep — runs from the 1 Hz {@link flush} loop so the
   * online → offline transition fires within ~1 s of crossing the threshold.
   *
   * Policy: if a unit's most recent finite `battery_kW` source-ts is older
   * than {@link BESS_BATTERY_KW_STALE_MS} (≥15 min), the BESS is offline
   * regardless of other keys it may still be publishing. Implemented as a
   * one-way transition (online → offline) per sweep — a fresh `battery_kW`
   * arriving via the WS handler is what flips the unit back to online (and
   * re-stamps the map). That keeps the policy crisp and avoids tile flicker
   * if a single straggler key arrives in a stale batch.
   *
   * DATA-06: for units past the threshold, `heartbeat_age_s` is re-derived
   * from (now − lastBatteryKwTs) on EVERY sweep — not written once at the
   * transition — so the tile's "last heartbeat X ago" keeps ticking instead
   * of freezing at the value it had when the unit went offline. Units with
   * no `battery_kW` history keep the initial 0, which BessTile renders as
   * "Offline · no recent data".
   */
  private evaluateBessStaleness(): void {
    const now = Date.now();
    let aggregateChange = false;
    for (const unit of this.snapshot.bess_per_unit) {
      const last = this.lastBatteryKwTs.get(unit.id);
      if (last === undefined) continue; // never received → leave initial state
      const ageMs = now - last;
      if (ageMs < BESS_BATTERY_KW_STALE_MS) continue;
      if (unit.online) {
        unit.online = false;
        aggregateChange = true;
      }
      const ageS = Math.round(ageMs / 1000);
      if (ageS !== unit.heartbeat_age_s) {
        unit.heartbeat_age_s = ageS;
        this.dirty = true;
      }
    }
    if (aggregateChange) {
      this.recomputeAggregate();
      this.dirty = true;
    }
  }

  /**
   * DATA-04 — computed connection quality:
   *   "offline" — the TB WebSocket is down (when the manager reports state).
   *   "stale"   — socket up, but the newest telemetry SOURCE ts is older
   *               than {@link LIVE_SOURCE_STALE_MS} (2× uplink cadence).
   *   "live"    — fresh telemetry within the window.
   * Before the first sample lands, age is measured from start() so a site
   * that never publishes degrades to "stale" instead of claiming "live".
   */
  private computeConnection(): ConnectionState {
    if (this.wsDown) return "offline";
    const newest = this.newestSourceTs || this.startedAt;
    return Date.now() - newest < LIVE_SOURCE_STALE_MS ? "live" : "stale";
  }

  private flush() {
    // Run the staleness sweep BEFORE the dirty-gate check: a unit silently
    // crossing the 15-min threshold produces no incoming WS traffic of its own
    // (that's the whole point), so without this the transition would never
    // dirty the snapshot and the tile would stay "online" indefinitely.
    this.evaluateBessStaleness();
    // Same reasoning for connection quality: going stale/offline is by
    // definition an absence of traffic, so it must dirty the snapshot here.
    const conn = this.computeConnection();
    if (conn !== this.snapshot.connection) {
      this.snapshot.connection = conn;
      this.dirty = true;
    }
    if (!this.dirty) return;
    this.dirty = false;
    // DATA-05: stamp from the newest telemetry SOURCE ts, never from the
    // flush wall-clock — attribute polls used to re-stamp ts every 30 s and
    // made minutes-old telemetry look perpetually fresh. Until the first
    // sample lands, keep the construction-time ts (honest "no data yet" age).
    if (this.newestSourceTs > 0) this.snapshot.ts = this.newestSourceTs;
    for (const l of this.listeners) l(this.snapshot);
  }

  private initialSnapshot(): SiteSnapshot {
    const t = this.site.topology;
    const totalCapacity = t.bess.reduce((s, b) => s + b.capacity_kWh, 0);
    const perUnit: BessUnitSnapshot[] = t.bess.map((b) => ({
      id: b.id,
      name: b.name,
      online: false,
      active_W: 0,
      reactive_VAr: 0,
      soc_pct: 0,
      total_voltage_V: 0,
      ambient_temperature_C: NaN,
      fault_count: 0,
      setpoint_W: 0,
      bess_onoff: false,
      heartbeat_age_s: 0,
    }));
    return {
      ts: Date.now(),
      connection: "live",
      // Live reference into the per-key source-ts map (DATA-08) — mutated
      // in place alongside the rest of the snapshot.
      freshness: this.freshness,
      grid: {
        active_W: 0, reactive_VAr: 0,
        voltage_V: NaN, current_A: NaN, frequency_Hz: NaN, pf: NaN,
      },
      bess_aggregate: {
        active_W: 0, reactive_VAr: 0,
        soc_avg_pct: 0, soc_min_pct: 0, soc_max_pct: 0,
        online_count: 0, fault_count: 0,
        total_capacity_kWh: totalCapacity,
        total_voltage_V: 0,
        avg_temperature_C: NaN,
      },
      bess_per_unit: perUnit,
      pv: this.site.refs.pv ? { active_W: 0, today_kWh: 0, rated_kWp: 0 } : undefined,
      building_load_W: 0,
      net_at_meter_W: 0,
      controller: {
        active_mode: "gridZeroControl",
        auto_bess_on: true,
        schedule_source: "cloud",
        pid_setpoint_W: 0,
        pid_actively_controlling: false,
        health: "ok",
      },
      health: {
        overall: "ok",
        services_active: 0, services_total: 0,
        cloud_uplink: "live",
        last_uplink_age_s: 0,
      },
      energy_today: {
        grid_import_kWh: 0, grid_export_kWh: 0,
        pv_kWh: 0, bess_charge_kWh: 0, bess_discharge_kWh: 0,
        self_consumption_pct: 0, self_sufficiency_pct: 0,
        money_saved_eur: 0, co2_avoided_kg: 0,
      },
    };
  }
}

function num(v: TsValue | undefined): number {
  if (!v) return NaN;
  if (typeof v.value === "number") return v.value;
  if (typeof v.value === "string") return Number(v.value);
  return NaN;
}
function readNum(attrs: Array<{ key: string; value: unknown }>, key: string): number | undefined {
  const a = attrs.find((x) => x.key === key);
  if (!a) return undefined;
  const v = typeof a.value === "number" ? a.value : typeof a.value === "string" ? Number(a.value) : NaN;
  return Number.isFinite(v) ? v : undefined;
}
function readStr(attrs: Array<{ key: string; value: unknown }>, key: string): string | undefined {
  const a = attrs.find((x) => x.key === key);
  if (!a) return undefined;
  return typeof a.value === "string" ? a.value : undefined;
}

/** Pick the first finite value from a list. Used to fall back across
 *  cloud-connector alias key names (e.g. dc_voltage / total_voltage). */
function firstFinite(values: number[]): number {
  for (const v of values) if (Number.isFinite(v)) return v;
  return NaN;
}
